import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert } from 'reactstrap';
import styled from 'styled-components';
import logo from '~/images/pp_logo.svg';
import { registerUser } from '../../../actions/user';
import { getInvitation } from '../../../actions/verify';
import Button from '../../atoms/Buttons';
import CheckBox from '../../atoms/CheckBox';
import Headings from '../../atoms/Headings';
import Label from '../../atoms/Label';
import TextBox from '../../atoms/TextBox';

type FormFields = {
  org_code: string;
  org_name: string;
  name: string;
  email: string;
  role: string;
  password: string;
  password_confirm: string;
  password_visible: boolean;
};

const PageWrapper = styled.div`
  padding: 0 1rem;
  display: flex;
  justify-content: center;
`;

const SectionWrapper = styled.div`
  padding: 4rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormWrapper = styled.div`
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${Button} {
    width: 5rem;
    min-width: 5rem;
    padding: 5px;
    box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
    -webkit-box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
    -moz-box-shadow: rgba(0, 0, 0, 0.3) 1px 2px 3px 0px;
    background: #007bff;
    color: white;
    &:hover {
      background: #0066cc;
    }
  }
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${Label} {
    margin-bottom: 0;
  }

  ${TextBox} {
    width: 22rem;
  }
`;

const CheckBoxWrapper = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const Logo = styled.img`
  margin-bottom: 0.5rem;
  align-self: start;
  height: 2.5rem;
`;

const SubLabel = styled(Label)`
  color: #9f9f9f;
  font-size: 0.8em;
`;

const InputError = styled.div`
  color: red;
`;

const VerifyUser = () => {
  const location = useLocation();
  const history = useHistory();

  const [isReset, setIsReset] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      org_code: '',
      org_name: '',
      name: '',
      email: '',
      role: '',
      password: '',
      password_confirm: '',
      password_visible: false,
    },
  });

  const queryId = new URLSearchParams(location.search).get('id');

  useEffect(() => {
    if (!queryId) return;

    getInvitation(queryId).then((json) => {
      reset({
        org_code: json.organization.code,
        org_name: json.organization.name,
        email: json.email,
      });

      setIsReset(json.is_reset);
    });
  }, [queryId, reset]);

  const onSubmit = async (data: FormFields) => {
    const response = await registerUser({ ...data, invitation_id: queryId });

    if (response.ok) {
      history.push(
        `/login?is_already_registered=true&organization=${data.org_code}&email=${encodeURIComponent(
          data.email
        )}`
      );
    } else {
      const json = await response.json();
      setError('root', {
        type: json.type || 'server_error',
        message: json.message || '登録に失敗しました。',
      });
    }
  };

  return (
    <PageWrapper>
      <SectionWrapper>
        <Logo src={logo} alt="Pokepay Logo" />
        <Headings.h5>新規登録</Headings.h5>
        <Label>{`組織「${watch('org_name')}」の管理 ${
          isReset ? 'ユーザのパスワードを再設定します。' : 'アカウントの登録を行ってください。'
        }`}</Label>
        {errors.root && (
          <Alert color="danger" isOpen>
            {errors.root.message}
            {errors.root.type === 'email_conflict' && (
              <a href="/login" className="alert-link">
                ログインページへはこちらから移動できます。
              </a>
            )}
          </Alert>
        )}
        <FormWrapper>
          <FieldWrapper>
            <Label>組織コード</Label>
            <Label>{watch('org_code')}</Label>
          </FieldWrapper>
          <FieldWrapper>
            <Label>メールアドレス</Label>
            <Label>{watch('email')}</Label>
          </FieldWrapper>

          <FieldWrapper>
            <Label>管理ユーザ名</Label>
            <TextBox
              type="text"
              {...register('name', { required: '必須入力です' })}
              placeholder="例）山田太郎"
            />
            {errors.name && <InputError>{errors.name.message}</InputError>}
          </FieldWrapper>
          <FieldWrapper>
            <Label>パスワード</Label>
            <TextBox
              type={watch('password_visible') ? 'text' : 'password'}
              {...register('password', {
                required: '必須入力です',
                minLength: {
                  value: 8,
                  message: '8文字以上で入力してください',
                },
              })}
            />
            <SubLabel>*8文字以上、半角英数・記号のみ</SubLabel>
            {errors.password && <InputError>{errors.password.message}</InputError>}
          </FieldWrapper>
          <FieldWrapper>
            <Label>パスワード(確認)</Label>
            <TextBox
              type={watch('password_visible') ? 'text' : 'password'}
              {...register('password_confirm', {
                required: '必須入力です',
                minLength: {
                  value: 8,
                  message: '8文字以上で入力してください',
                },
              })}
            />
            {errors.password_confirm && <InputError>{errors.password_confirm.message}</InputError>}
          </FieldWrapper>
          <CheckBoxWrapper>
            <CheckBox
              checked={watch('password_visible')}
              setChecked={(checked) => setValue('password_visible', checked)}
            />
            <Label>パスワードを表示</Label>
          </CheckBoxWrapper>
          <Button onClick={handleSubmit(onSubmit)}>登録</Button>
        </FormWrapper>
      </SectionWrapper>
    </PageWrapper>
  );
};

export default VerifyUser;
